import { useDispatch, useSelector } from "react-redux";
import { getErrorMessage } from "../../../utils/errorUtils";
import { isBooleanTrue, isListNotEmpty, isObjectNotEmpty, isStringNotEmpty } from "../../../utils/objectUtils";
import { getClientIPAddress } from "../../../utils/clientUtils";
import logger from "../../../utils/logger";
import { useCallback, useMemo } from "react";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { db } from "../../../firebase";
import Conversation from "../../../models/Conversation";
import ConversationMessage from "../../../models/ConversationMessage";
import Task from "../../../models/Task";
import AuthUser from "../../../models/AuthUser";
import { supportChatAI, createGuestSupportChat, autoAssignTicket } from "../../../firebase/Support";
import { ACCOUNT_AGREEMENT_KEYS, generateUserAgreements } from "../../../firebase/Agreements";
import moment from "moment-timezone";

//#region Initial State

const initialState = {
	// Chat widget state
	isWidgetOpen: false,
	isRegistering: false,

	// Active conversation
	activeConversationId: null,
	activeConversation: null,
	messages: [],

	// AI state
	isAiTyping: false,
	lastAiActions: [],

	// Support conversations list (for staff dashboard)
	conversations: [],

	isLoading: false,
	error: null
};

//#endregion
//#region supportReducer

/**
 * supportReducer - Manages all support chat state
 *
 * Handles widget open/close, active conversation tracking,
 * real-time message updates, AI typing state, and staff dashboard conversations.
 */
const supportReducer = (state = initialState, action) => {
	const { type, payload } = action;
	switch (type) {
		case 'SUPPORT_TOGGLE_WIDGET':
			return {
				...state,
				isWidgetOpen: !state.isWidgetOpen
			};
		case 'SUPPORT_OPEN_WIDGET':
			return {
				...state,
				isWidgetOpen: true
			};
		case 'SUPPORT_CLOSE_WIDGET':
			return {
				...state,
				isWidgetOpen: false
			};
		case 'SUPPORT_SET_REGISTERING':
			return {
				...state,
				isRegistering: payload
			};
		case 'SUPPORT_SET_ACTIVE_CONVERSATION':
			return {
				...state,
				activeConversationId: payload.conversationId,
				activeConversation: payload.conversation
			};
		case 'SUPPORT_LOAD_MESSAGES':
			return {
				...state,
				messages: payload
			};
		case 'SUPPORT_ADD_MESSAGE':
			return {
				...state,
				messages: [...state.messages, payload]
			};
		case 'SUPPORT_SET_AI_TYPING':
			return {
				...state,
				isAiTyping: payload
			};
		case 'SUPPORT_SET_AI_ACTIONS':
			return {
				...state,
				lastAiActions: payload
			};
		case 'SUPPORT_LOAD_CONVERSATIONS':
			return {
				...state,
				conversations: payload
			};
		case 'SUPPORT_SET_LOADING':
			return {
				...state,
				isLoading: payload
			};
		case 'SUPPORT_SET_ERROR':
			return {
				...state,
				error: payload
			};
		case 'SUPPORT_RESET':
			return {
				...initialState
			};
		default:
			return state;
	}
};

//#endregion
//#region widgetActions

/** Toggle the support chat widget open/closed */
export const toggleSupportWidget = () => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SUPPORT_TOGGLE_WIDGET' });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	};
};

/** Explicitly open the support chat widget */
export const openSupportWidget = () => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SUPPORT_OPEN_WIDGET' });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	};
};

/** Explicitly close the support chat widget */
export const closeSupportWidget = () => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SUPPORT_CLOSE_WIDGET' });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	};
};

//#endregion
//#region chatActions

/**
 * Register a guest user and create a support conversation.
 * Calls the createGuestSupportAccount Cloud Function, signs in with
 * the returned custom token, then starts listening to messages.
 */
export const registerGuestAndStartChat = (name, email, phone) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SUPPORT_SET_REGISTERING', payload: true });
			dispatch({ type: 'SUPPORT_SET_LOADING', payload: true });

			// Step 1: Create auth account + User doc on the frontend (signs in automatically)
			const accountResult = await AuthUser.createGuestSupportAccount(name, email, phone);

			if (accountResult?.exists) {
				dispatch({ type: 'SUPPORT_SET_ERROR', payload: 'An account with this email already exists. Please log in.' });
				dispatch({ type: 'SUPPORT_SET_REGISTERING', payload: false });
				dispatch({ type: 'SUPPORT_SET_LOADING', payload: false });
				return { exists: true };
			}

			if (accountResult?.success) {
				// the form will not submit until the agreements are accepted; record them as sign-up does, off the
				// critical path so a pdf failure never blocks the chat
				getClientIPAddress().then((ipAddress) => generateUserAgreements(accountResult.userId, ACCOUNT_AGREEMENT_KEYS, ipAddress, name)).catch((err) => logger.logReduxErrorEvent(err, getErrorMessage(err), true));

				// Step 2: Create the support conversation via Cloud Function (requires admin role in Firestore rules)
				const chatResult = await createGuestSupportChat(name, email);

				if (chatResult?.success) {
					dispatch({
						type: 'SUPPORT_SET_ACTIVE_CONVERSATION',
						payload: {
							conversationId: chatResult.conversationId,
							conversation: null // Will be loaded by listener
						}
					});

					dispatch({ type: 'SUPPORT_SET_REGISTERING', payload: false });
					dispatch({ type: 'SUPPORT_SET_LOADING', payload: false });

					// Start listening to the conversation
					dispatch(listenToSupportMessages(chatResult.conversationId));

					return { success: true, conversationId: chatResult.conversationId };
				}
			}

			dispatch({ type: 'SUPPORT_SET_LOADING', payload: false });
			dispatch({ type: 'SUPPORT_SET_REGISTERING', payload: false });
			return { success: false };

		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			dispatch({ type: 'SUPPORT_SET_ERROR', payload: 'Failed to create account. Please try again.' });
			dispatch({ type: 'SUPPORT_SET_LOADING', payload: false });
			dispatch({ type: 'SUPPORT_SET_REGISTERING', payload: false });
			return { success: false };
		}
	};
};

/**
 * Find or create a support conversation for an authenticated user.
 * First checks for an existing active CUSTOMER_SUPPORT conversation;
 * if none exists, creates a new one with AI handling enabled.
 */
export const startSupportChat = (userId, displayName, email) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SUPPORT_SET_LOADING', payload: true });

			// Look for existing active support conversation
			const existingConversations = await Conversation.getByField('supportMetadata.customerUserId', userId);
			const activeConversation = existingConversations?.find(
				c => c.type === 'CUSTOMER_SUPPORT' && c.status === 'ACTIVE' && !c.supportMetadata?.isResolved
			);

			if (isObjectNotEmpty(activeConversation)) {
				dispatch({
					type: 'SUPPORT_SET_ACTIVE_CONVERSATION',
					payload: {
						conversationId: activeConversation.id,
						conversation: activeConversation
					}
				});

				dispatch(listenToSupportMessages(activeConversation.id));
				dispatch({ type: 'SUPPORT_SET_LOADING', payload: false });
				return { conversationId: activeConversation.id };
			}

			// Create new support conversation
			const newConversation = new Conversation({
				name: `Support - ${displayName || 'Customer'}`,
				type: 'CUSTOMER_SUPPORT',
				participantIds: [userId],
				adminIds: [userId],
				visibility: 'PRIVATE',
				supportMetadata: {
					ticketId: null,
					assignedAgentId: null,
					aiEnabled: true,
					aiHandoffReason: null,
					isAiHandling: true,
					customerUserId: userId,
					customerName: displayName || null,
					customerEmail: email || null,
					isResolved: false,
					resolvedAt: null,
					resolvedBy: null
				}
			});

			const conversationId = await newConversation.create();

			dispatch({
				type: 'SUPPORT_SET_ACTIVE_CONVERSATION',
				payload: {
					conversationId,
					conversation: { ...newConversation.toDto(), id: conversationId }
				}
			});

			dispatch(listenToSupportMessages(conversationId));
			dispatch({ type: 'SUPPORT_SET_LOADING', payload: false });
			return { conversationId };

		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			dispatch({ type: 'SUPPORT_SET_ERROR', payload: 'Failed to start support chat.' });
			dispatch({ type: 'SUPPORT_SET_LOADING', payload: false });
			return null;
		}
	};
};

/**
 * Send a user message and get the AI response via the supportChatAI Cloud Function.
 * Sets typing indicator while waiting, and stores any AI actions (ticket creation, escalation, etc.).
 */
export const sendSupportMessage = (conversationId, messageContent) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SUPPORT_SET_AI_TYPING', payload: true });
			dispatch({ type: 'SUPPORT_SET_ERROR', payload: null });

			const result = await supportChatAI(conversationId, messageContent);

			dispatch({ type: 'SUPPORT_SET_AI_TYPING', payload: false });

			if (result?.success) {
				if (isListNotEmpty(result.actions)) {
					dispatch({ type: 'SUPPORT_SET_AI_ACTIONS', payload: result.actions });
				}

				return result;
			}

			return null;

		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			dispatch({ type: 'SUPPORT_SET_AI_TYPING', payload: false });
			dispatch({ type: 'SUPPORT_SET_ERROR', payload: 'Failed to send message. Please try again.' });
			return null;
		}
	};
};

/**
 * Listen to support conversation messages in real-time via Firestore onSnapshot.
 * Dispatches SUPPORT_LOAD_MESSAGES whenever the messages collection changes.
 */
export const listenToSupportMessages = (conversationId) => {
	return async (dispatch) => {
		try {
			if (isStringNotEmpty(conversationId)) {
				const messagesQuery = query(
					collection(db, 'conversationMessages'),
					where('conversationId', '==', conversationId),
					orderBy('sentAt', 'asc')
				);

				onSnapshot(messagesQuery, (snapshot) => {
					const messages = snapshot.docs.map((doc) => ({
						id: doc.id,
						...doc.data()
					}));

					dispatch({ type: 'SUPPORT_LOAD_MESSAGES', payload: messages });
				}, (error) => {
					console.error('Support messages listener error:', error);
				});
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	};
};

/**
 * Load all support conversations (for the staff dashboard view)
 */
export const loadSupportConversations = () => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SUPPORT_SET_LOADING', payload: true });

			const conversations = await Conversation.getByField('type', 'CUSTOMER_SUPPORT');
			dispatch({ type: 'SUPPORT_LOAD_CONVERSATIONS', payload: conversations || [] });

			dispatch({ type: 'SUPPORT_SET_LOADING', payload: false });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			dispatch({ type: 'SUPPORT_SET_LOADING', payload: false });
		}
	};
};

//#endregion
//#region staffActions

/**
 * Update a support ticket's status (staff action)
 */
export const updateTicketStatus = (ticketId, newStatus, ticket) => {
	return async (dispatch) => {
		try {
			const updated = await Task.updateStatus(ticketId, newStatus, '', ticket);
			return updated;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return null;
		}
	};
};

/**
 * Assign a support ticket to a specific rep and update the linked conversation
 */
export const assignTicket = (ticketId, repUserId, conversationId) => {
	return async (dispatch) => {
		try {
			await Task.updateFields(ticketId, {
				assignedTo: repUserId,
				assignedAt: moment().utc().toDate()
			});

			if (isStringNotEmpty(conversationId)) {
				await Conversation.updateField(conversationId, 'supportMetadata.assignedAgentId', repUserId);
			}

			return true;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return false;
		}
	};
};

/**
 * Escalate a ticket to a higher support level with a reason
 */
export const escalateTicket = (ticketId, newLevel, reason, currentUserId) => {
	return async (dispatch) => {
		try {
			await Task.updateFields(ticketId, {
				escalationLevel: newLevel,
				escalatedAt: moment().utc().toDate(),
				escalatedBy: currentUserId,
				escalationReason: reason || null
			});
			return true;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return false;
		}
	};
};

/**
 * Resolve a ticket and mark the linked conversation as resolved
 */
export const resolveTicket = (ticketId, ticket, currentUserId) => {
	return async (dispatch) => {
		try {
			const updated = await Task.updateStatus(ticketId, 'COMPLETED', 'Resolved by staff', ticket);

			if (isStringNotEmpty(ticket?.sourceConversationId)) {
				await Conversation.updateFields(ticket.sourceConversationId, {
					'supportMetadata.isResolved': true,
					'supportMetadata.resolvedAt': moment().utc().toDate(),
					'supportMetadata.resolvedBy': currentUserId
				});
			}

			return updated;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return null;
		}
	};
};

/**
 * Send a message as a staff member in a support conversation.
 * Also marks the conversation as human-handled (disables AI).
 */
export const sendStaffMessage = (conversationId, messageContent, staffProfile) => {
	return async (dispatch) => {
		try {
			const newMessage = new ConversationMessage({
				conversationId,
				authorId: staffProfile.userId,
				type: 'TEXT',
				content: { raw: messageContent },
				metadata: {
					authorName: staffProfile.displayName || `${staffProfile.firstName} ${staffProfile.lastName}`
				}
			});

			await newMessage.create();

			// Update conversation's lastMessage and mark as human-handled
			await Conversation.updateFields(conversationId, {
				lastMessage: {
					raw: messageContent,
					authorId: staffProfile.userId,
					sentAt: moment().utc().toDate()
				},
				lastActiveAt: moment().utc().toDate(),
				'supportMetadata.isAiHandling': false
			});

			return true;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return false;
		}
	};
};

/**
 * Auto-assign a ticket to the best available rep via the autoAssignTicket Cloud Function.
 * Uses round-robin load balancing based on open ticket count.
 */
export const autoAssignTicketAction = (ticketId) => {
	return async (dispatch) => {
		try {
			const result = await autoAssignTicket(ticketId);
			return result;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return null;
		}
	};
};

/**
 * Reset all support state back to initial values (e.g., on logout)
 */
export const resetSupport = () => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SUPPORT_RESET' });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	};
};

//#endregion
//#region useSupport

/**
 * useSupport - Custom hook providing access to all support state and dispatched actions.
 * Components use this hook instead of directly accessing the Redux store.
 */
export const useSupport = () => {
	const dispatch = useDispatch();

	const isWidgetOpen = useSelector((appState) => appState.supportState.isWidgetOpen);
	const isRegistering = useSelector((appState) => appState.supportState.isRegistering);
	const activeConversationId = useSelector((appState) => appState.supportState.activeConversationId);
	const activeConversation = useSelector((appState) => appState.supportState.activeConversation);
	const messages = useSelector((appState) => appState.supportState.messages);
	const isAiTyping = useSelector((appState) => appState.supportState.isAiTyping);
	const lastAiActions = useSelector((appState) => appState.supportState.lastAiActions);
	const conversations = useSelector((appState) => appState.supportState.conversations);
	const isLoading = useSelector((appState) => appState.supportState.isLoading);
	const error = useSelector((appState) => appState.supportState.error);

	// Derived state - computed from raw state values

	/** Whether there is an active conversation (conversationId is set) */
	const hasActiveConversation = useMemo(() => {
		return isStringNotEmpty(activeConversationId);
	}, [activeConversationId]);

	/** Whether the AI triggered a redirect to doctor chat */
	const hasDoctorRedirect = useMemo(() => {
		return lastAiActions?.some(a => a.type === 'redirect_to_doctor_chat') || false;
	}, [lastAiActions]);

	/** Whether the AI escalated the conversation to a human agent */
	const hasEscalation = useMemo(() => {
		return lastAiActions?.some(a => a.type === 'escalated_to_human') || false;
	}, [lastAiActions]);

	/** The ticket_created action object (if a ticket was created), or null */
	const hasTicketCreated = useMemo(() => {
		return lastAiActions?.find(a => a.type === 'ticket_created') || null;
	}, [lastAiActions]);

	return {
		SupportState: useSelector((appState) => appState.supportState),

		// Widget state
		isWidgetOpen,
		isRegistering,

		// Conversation state
		activeConversationId,
		activeConversation,
		messages,
		hasActiveConversation,

		// AI state
		isAiTyping,
		lastAiActions,
		hasDoctorRedirect,
		hasEscalation,
		hasTicketCreated,

		// Staff dashboard
		conversations,

		// Loading/error
		isLoading,
		error,

		// Widget actions
		toggleSupportWidget: () => dispatch(toggleSupportWidget()),
		openSupportWidget: () => dispatch(openSupportWidget()),
		closeSupportWidget: () => dispatch(closeSupportWidget()),
		registerGuestAndStartChat: (name, email, phone) => dispatch(registerGuestAndStartChat(name, email, phone)),
		startSupportChat: (userId, displayName, email) => dispatch(startSupportChat(userId, displayName, email)),
		sendSupportMessage: (conversationId, messageContent) => dispatch(sendSupportMessage(conversationId, messageContent)),
		listenToSupportMessages: (conversationId) => dispatch(listenToSupportMessages(conversationId)),
		loadSupportConversations: () => dispatch(loadSupportConversations()),
		resetSupport: () => dispatch(resetSupport()),

		// Staff actions
		updateTicketStatus: (ticketId, newStatus, ticket) => dispatch(updateTicketStatus(ticketId, newStatus, ticket)),
		assignTicket: (ticketId, repUserId, conversationId) => dispatch(assignTicket(ticketId, repUserId, conversationId)),
		escalateTicket: (ticketId, newLevel, reason, currentUserId) => dispatch(escalateTicket(ticketId, newLevel, reason, currentUserId)),
		resolveTicket: (ticketId, ticket, currentUserId) => dispatch(resolveTicket(ticketId, ticket, currentUserId)),
		sendStaffMessage: (conversationId, messageContent, staffProfile) => dispatch(sendStaffMessage(conversationId, messageContent, staffProfile)),
		autoAssignTicket: (ticketId) => dispatch(autoAssignTicketAction(ticketId))
	};
};

//#endregion

export default supportReducer;
